/**
 * Frontend API client — calls Pages Functions at /api/*
 */

import type { UploadResponse, StatusResponse } from '../types/scan'

const BASE = '/api'

// ── Upload JAR ────────────────────────────────────────────────────────────────

export async function uploadJar(
  file: File,
  onProgress?: (percent: number) => void
): Promise<UploadResponse> {
  const formData = new FormData()
  formData.append('file', file)

  // Use XMLHttpRequest for upload progress tracking
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as UploadResponse)
        } catch {
          reject(new Error('Invalid response from server'))
        }
      } else {
        let message = `Upload failed (${xhr.status})`
        try {
          const errBody = JSON.parse(xhr.responseText)
          message = errBody.error ?? message
        } catch {
          // ignore parse error
        }
        reject(new Error(message))
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')))
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')))

    xhr.open('POST', `${BASE}/upload`)
    xhr.send(formData)
  })
}

// ── Trigger scan ──────────────────────────────────────────────────────────────

export async function startScan(scanId: string, fileKey: string): Promise<void> {
  const response = await fetch(`${BASE}/scan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scanId, fileKey }),
  })

  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? `Scan start failed (${response.status})`)
  }
}

// ── Poll scan status ──────────────────────────────────────────────────────────

export async function getScanStatus(scanId: string): Promise<StatusResponse> {
  const response = await fetch(`${BASE}/scan/${scanId}/status`)
  if (!response.ok) {
    if (response.status === 404) throw new Error('Scan not found')
    throw new Error(`Status check failed (${response.status})`)
  }
  return response.json() as Promise<StatusResponse>
}

/**
 * Poll status until complete or failed.
 * Calls onUpdate with each status response.
 * Resolves when status is 'complete', rejects when 'failed'.
 */
export async function pollUntilDone(
  scanId: string,
  onUpdate: (status: StatusResponse) => void,
  intervalMs = 2000
): Promise<StatusResponse> {
  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const status = await getScanStatus(scanId)
        onUpdate(status)

        if (status.status === 'complete') {
          resolve(status)
          return
        }
        if (status.status === 'failed') {
          reject(new Error(status.result?.error ?? 'Scan failed'))
          return
        }

        setTimeout(poll, intervalMs)
      } catch (err) {
        reject(err)
      }
    }

    poll()
  })
}
